import * as React from "react";
import {
    Area,
    AreaChart,
    CartesianGrid,
    XAxis,
    Tooltip,
    ResponsiveContainer,
} from "recharts";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "../../../components/ui/card";

export type SeriesPoint = [string, number];
export interface ShipmentSeries {
    id: string;
    label: string;
    points: SeriesPoint[];
}

interface ShipmentChartProps {
    seriesData?: ShipmentSeries[];
    loading?: boolean;
}

interface ChartConfigItem {
    label: string;
    color: string;
}

type ChartConfig = Record<string, ChartConfigItem>;

interface ChartDataPoint {
    date: string;
    [key: string]: string | number;
}

export const ShipmentChart: React.FC<ShipmentChartProps> = ({
    seriesData = [],
    loading = false,
}) => {
    const availableSeries = seriesData;
    const [activeChart, setActiveChart] = React.useState<string>("");

    const chartConfig = React.useMemo<ChartConfig>(() => {
        const config: ChartConfig = {};
        const colors = [
            "hsl(220 70% 50%)",
            "hsl(160 60% 45%)",
            "hsl(280 65% 60%)",
            "hsl(30 80% 55%)",
            "hsl(340 75% 55%)",
        ];

        availableSeries.forEach((series, index) => {
            if (!config[series.id]) {
                config[series.id] = {
                    label: series.label,
                    color: colors[index % colors.length],
                };
            }
        });
        return config;
    }, [availableSeries]);


    const chartData = React.useMemo<ChartDataPoint[]>(() => {
        if (!availableSeries.length) return [];

        const allDates = new Set<string>();
        availableSeries.forEach((series) => {
            series.points.forEach(([date]) => allDates.add(date));
        });

        const sortedDates = Array.from(allDates).sort();
        return sortedDates.map((date) => {
            const dataPoint: ChartDataPoint = { date };

            availableSeries.forEach((series) => {
                const point = series.points.find(
                    ([pointDate]) => pointDate === date
                );
                dataPoint[series.id] = point ? point[1] : 0;
            });
            return dataPoint;
        });
    }, [availableSeries]);

    const totals = React.useMemo<Record<string, number>>(() => {
        const result: Record<string, number> = {};
        availableSeries.forEach((series) => {
            result[series.id] = series.points.reduce(
                (acc, [, value]) => acc + value,
                0
            );
        });
        return result;
    }, [availableSeries]);

    React.useEffect(() => {
        if (availableSeries.length > 0) {
            if (
                !activeChart ||
                !availableSeries.some((s) => s.id === activeChart)
            ) {
                setActiveChart(availableSeries[0].id);
            }
        }
    }, [availableSeries, activeChart]);


    if (loading || availableSeries.length === 0) {
        return (
            <Card>
                <CardHeader className="flex flex-col items-stretch border-b p-0 sm:flex-row">
                    <div className="flex flex-1 flex-col justify-center gap-1 px-4 py-3 sm:py-4">
                        <CardTitle className="text-base">Shipment Analytics</CardTitle>
                        <CardDescription className="text-xs">
                            {loading ? "Loading chart data..." : "No data available"}
                        </CardDescription>
                    </div>
                </CardHeader>
                <CardContent className="px-2 py-4 sm:p-4">
                    <div className="h-[220px] w-full flex items-center justify-center">
                        <div className="text-muted-foreground">
                            {loading ? "Loading..." : "No data to display"}
                        </div>
                    </div>
                </CardContent>
            </Card>
        );
    }

    if (!activeChart || !chartData.length) {
        return (
            <Card>
                <CardHeader className="flex flex-col items-stretch border-b p-0 sm:flex-row">
                    <div className="flex flex-1 flex-col justify-center gap-1 px-4 py-3 sm:py-4">
                        <CardTitle className="text-base">Shipment Analytics</CardTitle>
                        <CardDescription className="text-xs">
                            Preparing chart...
                        </CardDescription>
                    </div>
                </CardHeader>
                <CardContent className="px-2 py-4 sm:p-4">
                    <div className="h-[220px] w-full flex items-center justify-center">
                        <div className="text-muted-foreground">Preparing data...</div>
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card>
            <CardHeader className="flex flex-col items-stretch border-b p-0 sm:flex-row">
                <div className="flex flex-1 flex-col justify-center gap-1 px-4 py-3 sm:py-4">
                    <CardTitle className="text-base">Shipment Analytics</CardTitle>
                    <CardDescription className="text-xs">
                        Daily shipment trends
                    </CardDescription>
                </div>

                <div className="flex">
                    {availableSeries.map((series) => (
                        <button
                            key={series.id}
                            data-active={activeChart === series.id}
                            className="data-[active=true]:bg-muted/50 relative flex flex-1 flex-col justify-center gap-1 border-t px-4 py-3 text-left even:border-l sm:border-t-0 sm:border-l sm:px-6 sm:py-4"
                            onClick={() => setActiveChart(series.id)}
                        >
                            <span className="text-muted-foreground text-xs">
                                {chartConfig[series.id]?.label || series.label}
                            </span>
                            <span className="text-lg font-bold leading-none sm:text-xl">
                                {(totals[series.id] ?? 0).toLocaleString()}
                            </span>
                        </button>
                    ))}
                </div>
            </CardHeader>

            <CardContent className="px-2 py-4 sm:p-4">
                <div className="h-[220px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart
                            data={chartData}
                            margin={{ left: 12, right: 12, top: 12, bottom: 12 }}
                        >
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />

                            <XAxis
                                dataKey="date"
                                tickLine={false}
                                axisLine={false}
                                tickMargin={8}
                                fontSize={11}
                                tickFormatter={(value: string) =>
                                    new Date(value).toLocaleDateString("en-US", {
                                        month: "short",
                                        day: "numeric",
                                    })
                                }
                            />

                            <Tooltip
                                content={(props) => {
                                    const { active, payload, label } = props as unknown as {
                                        active?: boolean;
                                        payload?: readonly { value?: number }[];
                                        label?: string;
                                    };

                                    if (active && payload && payload.length && label) {
                                        return (
                                            <div className="rounded-lg border bg-background p-2 shadow-sm">
                                                <div className="mb-1 text-xs font-medium">
                                                    {new Date(label).toLocaleDateString("en-US", {
                                                        month: "short",
                                                        day: "numeric",
                                                        year: "numeric",
                                                    })}
                                                </div>

                                                <div className="text-xs">
                                                    <span className="text-muted-foreground">
                                                        {chartConfig[activeChart]?.label || activeChart}:
                                                    </span>
                                                    <span className="ml-1 font-medium">
                                                        {payload[0]?.value?.toLocaleString()}
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    }

                                    return null;
                                }}
                            />

                            <Area
                                dataKey={activeChart}
                                stroke={chartConfig[activeChart]?.color}
                                fill={chartConfig[activeChart]?.color}
                                fillOpacity={0.2}
                                strokeWidth={2}
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </CardContent>
        </Card>
    );
};
